package co.uk.paxes.pinpoint.web

import co.uk.paxes.pinpoint.model.*
import co.uk.paxes.pinpoint.engine.GameEngine
import co.uk.paxes.pinpoint.engine.GameEngineListener
import co.uk.paxes.pinpoint.web.ads.FacebookAdService
import co.uk.paxes.pinpoint.web.ads.UnlockStateManager
import co.uk.paxes.pinpoint.web.ads.UndoCreditManager
import co.uk.paxes.pinpoint.web.ads.InterstitialAdManager
import co.uk.paxes.pinpoint.web.platform.PlatformDetector
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.*
import org.w3c.dom.events.MouseEvent
import kotlin.random.Random
import kotlin.js.JsExport
import kotlin.js.JsName

/**
 * Manages AI single-player games.
 * Handles game initialization, turn management, and AI move execution.
 * 
 * Requirements: 16.1, 16.2, 16.3, 16.4, 16.5
 */
object AIGameManager {
    
    // Game state
    private var gameEngine: GameEngine? = null
    private var aiPlayer: AIPlayer? = null
    private var difficulty: String = "EASY"
    
    /**
     * Get the difficulty emoji based on difficulty level.
     */
    private fun getDifficultyEmoji(difficultyLevel: String): String {
        return when (difficultyLevel.uppercase()) {
            "EASY" -> "😊"
            "MEDIUM" -> "🤔"
            "HARD" -> "😤"
            "EXPERT" -> "🧠"
            else -> "🤖"
        }
    }
    private var isPlayerTurn: Boolean = true
    private var isGameOver: Boolean = false
    
    // Game pause state for ads
    private var isGamePaused: Boolean = false
    private var pendingAITimeoutId: Int? = null
    
    // Player info
    private const val PLAYER_ID = "human-player"
    private const val PLAYER_NAME = "You"
    
    // Hand state
    private var selectedPinIndex: Int = 0
    private var pendingJokerIndex: Int? = null  // Track which pin is being sacrificed for joker
    private var pendingJokerPin: Pin? = null    // The joker pin waiting to be placed
    
    // Undo state
    // Undo credits are managed by RewardedAdManager
    // private var undoCredits: Int = 0
    private var lastPlayerMove: Move? = null
    private var lastPlayerMoveState: GameState? = null  // State before the move
    private var canUndo: Boolean = false
    
    // Score tracking for display
    private var lastPinScore: Int? = null
    private var handScore: Int = 0
    private val scoreCalculator = ScoreCalculator()
    private var previousHandSize: Int = 4  // Track hand size to detect new hand
    
    // Countdown timer state
    private var isInHandCompletionPause: Boolean = false
    private var handCompletionPauseTimeRemaining: Int = 0
    private var countdownIntervalId: Int? = null
    private var turnTransitionTimeoutId: Int? = null
    
    // Score Flash Animation state
    private var flashingPositions: Set<Position> = emptySet()
    private var isFlashVisible: Boolean = false
    private var scoreFlashIntervalId: Int? = null
    private var scoreFlashTimeoutId: Int? = null
    
    // Unlocked difficulties (permanent - stored with expiration times)
    private val unlockedDifficulties = mutableSetOf(AIDifficulty.EASY, AIDifficulty.MEDIUM)
    
    // Session-only unlocks (cleared when page refreshes)
    private val sessionUnlocks: MutableSet<AIDifficulty> = mutableSetOf()
    
    // Time-based unlock durations (like Android app)
    private const val HARD_UNLOCK_DURATION = 6 * 60 * 60 * 1000L // 6 hours in milliseconds
    private const val EXPERT_UNLOCK_DURATION = 12 * 60 * 60 * 1000L // 12 hours in milliseconds
    
    // Pin colors for rendering
    private val PIN_COLORS = mapOf(
        PinColor.RED to "#FF6B6B",
        PinColor.GREEN to "#00FF00",
        PinColor.PURPLE to "#8B00FF",
        PinColor.YELLOW to "#FFFF00",
        PinColor.BLACK to "#000000"
    )
    
    /**
     * Initialize the AI game manager.
     */
    fun initialize() {
        loadUnlockedDifficulties()
        
        // Initialize undo credits
        co.uk.paxes.pinpoint.web.ads.UndoCreditManager.initialize()
        
        // Initialize banner ads
        co.uk.paxes.pinpoint.web.BannerAdManager.initialize()
        
        // Export functions to window for JavaScript bridge (same pattern as Main.kt)
        val w = window.asDynamic()
        w.pinpointSelectAIDifficulty = { d: String -> selectDifficulty(d) }
        w.pinpointQuitAIGame = { quitGame() }
        w.pinpointSelectAIHandPin = { i: Int -> selectHandPin(i) }
        w.aiSacrificePinForJoker = { i: Int -> sacrificePinForJoker(i) }
        w.aiSelectJokerColor = { c: String -> selectJokerColor(c) }
        w.aiCancelJoker = { cancelJoker() }
        w.aiRequestUndo = { requestUndo() }
        w.aiWatchAdForCredit = { watchAdForCredit() }
        w.refreshAIDifficultyLocks = { refreshAIDifficultyLocks() }
        
        // REDRAW FIX: Listen for canvas resize events to redraw the board
        window.addEventListener("canvasResized", {
            if (NavigationManager.getCurrentScreen() == Screen.AI_GAME) {
                console.log("AIGameManager: Canvas resized - redrawing board")
                renderAIBoard()
                renderAIHand()
            }
        })

        // Load undo credits
        loadUndoCredits()
        
        console.log("AIGameManager initialized")
    }
    
    /**
     * Refresh the UI to show/hide lock icons based on current unlock state.
     * Called after unlocking a difficulty to update the UI.
     */
    fun refreshAIDifficultyLocks() {
        console.log("AIGameManager: Refreshing difficulty lock UI")
        
        // Call the JavaScript function that updates the UI
        val jsFunction = window.asDynamic().refreshAIDifficultyLocksUI
        if (jsFunction != null) {
            jsFunction()
        } else {
            console.warn("AIGameManager: refreshAIDifficultyLocksUI JavaScript function not found")
        }
    }
    
    /**
     * Select a difficulty level and start the game.
     * Shows rewarded ad for locked difficulties.
     * 
     * Requirements: 16.1, 16.3, 16.4
     */
    fun selectDifficulty(difficultyName: String) {
        val difficulty = when (difficultyName.lowercase()) {
            "easy" -> AIDifficulty.EASY
            "medium" -> AIDifficulty.MEDIUM
            "hard" -> AIDifficulty.HARD
            "expert" -> AIDifficulty.EXPERT
            else -> AIDifficulty.EASY
        }
        
        console.log("AIGameManager: Selected difficulty $difficulty")
        
        // Check if difficulty is unlocked
        if (!UnlockStateManager.isUnlocked(difficulty)) {
            console.log("AIGameManager: Difficulty $difficulty is locked, showing ad...")
            
            // Show rewarded ad to unlock using Adsterra via RewardedAdManager
            RewardedAdManager.showRewardedAd(
                adName = "unlock_${difficulty.name.lowercase()}",
                onRewardEarned = {
                    console.log("AIGameManager: Ad completed, unlocking $difficulty permanently")
                    UnlockStateManager.unlockPermanently(difficulty)
                    refreshAIDifficultyLocks()
                    startGame(difficulty)
                },
                onAdClosed = {
                    console.log("AIGameManager: Ad flow closed")
                },
                onAdFailed = {
                    console.log("AIGameManager: Ad failed - unlocking for session only")
                    UnlockStateManager.unlockForSession(difficulty)
                    window.alert("Ad unavailable. ${difficulty.displayName} unlocked for this session.")
                    startGame(difficulty)
                }
            )
        } else {
            console.log("AIGameManager: Difficulty $difficulty is already unlocked")
            startGame(difficulty)
        }
    }
    
    /**
     * Start a new AI game with the specified difficulty.
     * 
     * Requirements: 16.5
     */
    fun startGame(difficulty: AIDifficulty) {
        console.log("AIGameManager: Starting game with difficulty $difficulty")
        
        this.difficulty = difficulty.name.uppercase()
        isPlayerTurn = true
        isGameOver = false
        selectedPinIndex = 0
        
        // Reset score tracking
        lastPinScore = null
        handScore = 0
        previousHandSize = 4
        
        // Reset interstitial ad counter
        InterstitialAdManager.reset()
        
        // Create AI Player instance
        val aiName = difficulty.getHALName()
        aiPlayer = AIPlayer(id = "ai-player", name = aiName, difficulty = this.difficulty)
        console.log("AIGameManager: Created AIPlayer with strategy: ${this.difficulty} (Logic v${ExpertStrategy.VERSION})")
        
        // Create players
        val humanPlayer = Player(id = PLAYER_ID, name = PLAYER_NAME, isAI = false)
        val aiOpponentPlayer = Player(id = aiPlayer!!.id, name = aiPlayer!!.name, isAI = true, aiDifficulty = this.difficulty)
        
        // Create game config
        val seed = kotlin.js.Date.now().toLong()
        val config = GameConfig(
            players = listOf(humanPlayer, aiOpponentPlayer),
            startingPlayerId = PLAYER_ID,
            boardSeed = seed,
            isNetworkClient = false
        )
        
        // Create game engine
        val engine = GameEngine(listener = object : GameEngineListener {
            override fun onGameStarted(state: GameState) {
                console.log("AIGameManager: Game started")
                renderAIBoard()
                renderAIScores()
                renderAIHand()
                updateAITurnIndicator()
                renderScoreDisplay()
            }
            
            override fun onMoveApplied(state: GameState, move: Move) {
                console.log("AIGameManager: Move applied by ${move.playerId}, hand size: ${state.currentHand.size}")
                
                // Update turn state FIRST based on the NEW state
                isPlayerTurn = state.currentPlayerId == PLAYER_ID
                console.log("AIGameManager: isPlayerTurn = $isPlayerTurn")
                
                // Check if a new hand was dealt for the player (hand size went from 0/1 to 4)
                val isNewHand = state.currentHand.size == 4 && previousHandSize <= 1
                if (isNewHand && isPlayerTurn) {
                    console.log("AIGameManager: New hand dealt for player, resetting hand score")
                    handScore = 0
                    lastPinScore = null
                    
                    // Hand completed - notify InterstitialAdManager
                    InterstitialAdManager.onHandComplete()
                    
                    // Trigger ad based on platform (Facebook Audience Network or AdSense)
                    try {
                        if (co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.isFacebook()) {
                            console.log("AIGameManager: Showing Facebook Audience Network ad")
                            // Facebook mode - use Audience Network
                            // Note: FacebookAdService methods are suspend functions, but we don't have coroutines
                            // For now, just log that we would show a Facebook ad
                            console.warn("Facebook Audience Network ads not yet implemented (requires coroutines)")
                        } else {
                            // Standalone mode - use AdSense H5 Ad Break
                            H5AdManager.triggerNextBreak("hand_complete")
                        }
                    } catch (e: Exception) {
                        console.error("Failed to show ad, falling back to AdSense", e)
                        H5AdManager.triggerNextBreak("hand_complete")
                    }
                    
                    // LEGACY: Granting free credits removed to enforce ad-based system
                }
                previousHandSize = state.currentHand.size
                
                // Track player's move for undo (only if player just moved)
                if (move.playerId == PLAYER_ID) {
                    canUndo = true
                    lastPlayerMove = move
                } else {
                    // AI moved, can't undo anymore
                    canUndo = false
                    lastPlayerMove = null
                    lastPlayerMoveState = null
                }
                
                // Render UI
                renderAIBoard()
                renderAIScores()
                renderAIHand()
                updateUndoUI()
                renderScoreDisplay()
                
                // Check for game over
                if (state.board.isFull()) {
                    handleGameOver(state)
                } else {
                    // If a hand was just completed, add a 3-second pause with countdown
                    if (isNewHand && isPlayerTurn) {
                        console.log("AIGameManager: Hand completed, starting 3-second countdown...")
                        
                        // Set countdown state
                        isInHandCompletionPause = true
                        handCompletionPauseTimeRemaining = 3
                        
                        // Update turn indicator immediately to show "3s"
                        updateAITurnIndicator()
                        
                        // Start countdown interval (updates every second)
                        countdownIntervalId = window.setInterval({
                            handCompletionPauseTimeRemaining--
                            console.log("AIGameManager: Countdown: ${handCompletionPauseTimeRemaining}s remaining")
                            updateAITurnIndicator()
                            
                            if (handCompletionPauseTimeRemaining <= 0) {
                                // Countdown finished, clear interval
                                countdownIntervalId?.let { window.clearInterval(it) }
                                countdownIntervalId = null
                                isInHandCompletionPause = false
                            }
                        }, 1000)
                        
                        // Store timeout ID so we can cancel it if Undo is pressed
                        turnTransitionTimeoutId = window.setTimeout({
                            turnTransitionTimeoutId = null
                            console.log("AIGameManager: Undo pause ended, continuing to next turn")
                            isInHandCompletionPause = false
                            handCompletionPauseTimeRemaining = 0
                            updateAITurnIndicator()
                            
                            // If it's AI's turn, make a move after delay
                            if (!isPlayerTurn && !isGameOver) {
                                scheduleAIMove()
                            }
                        }, 3000)
                    } else {
                        updateAITurnIndicator()
                        
                        // If it's AI's turn, make a move after delay
                        if (!isPlayerTurn && !isGameOver) {
                            scheduleAIMove()
                        }
                    }
                }
            }
            
            override fun onGameEnded(state: GameState) {
                console.log("AIGameManager: Game ended")
                handleGameOver(state)
            }
        })
        
        gameEngine = engine
        engine.startGame(config)
        
        // Navigate to AI game screen
        NavigationManager.navigateTo(Screen.AI_GAME)
        
        // Set up board click handler after navigation (canvas now exists)
        window.setTimeout({
            setupBoardClickHandler()
        }, 100)
    }
    
    /**
     * Schedule AI move with appropriate delay.
     */
    private fun scheduleAIMove() {
        // Don't schedule if game is paused
        if (isGamePaused) {
            console.log("AIGameManager: Game paused - deferring AI move")
            return
        }
        
        val delay = when (difficulty.uppercase()) {
            "EASY" -> 500
            "MEDIUM" -> 800
            "HARD" -> 1000
            "EXPERT" -> 1200
            else -> 1000
        }
        
        // Show thinking indicator
        val indicator = document.getElementById("ai-turn-indicator")
        indicator?.textContent = "🤔 AI is thinking..."
        indicator?.classList?.add("thinking")
        
        pendingAITimeoutId = window.setTimeout({
            pendingAITimeoutId = null
            makeAIMove()
        }, delay)
    }
    
    /**
     * Make the AI's move.
     */
    private fun makeAIMove() {
        if (isGameOver || isPlayerTurn) return
        
        val engine = gameEngine ?: return
        val ai = aiPlayer ?: return
        val state = engine.getCurrentGameState() ?: return
        
        // Get AI's hand
        val aiHand = state.currentHand
        if (aiHand.isEmpty()) {
            console.log("AIGameManager: AI has no pins in hand")
            return
        }
        
        // INVESTIGATION: Log AI player joker state
        console.log("🔍 AIGameManager.makeAIMove() - INVESTIGATION:")
        console.log("  - AI difficulty: ${ai.difficulty}")
        console.log("  - AI player ID: ${ai.id}")
        console.log("  - Current player ID: ${state.currentPlayerId}")
        console.log("  - Current player: ${state.currentPlayer.name}")
        console.log("  - Current player jokersUsed: ${state.currentPlayer.jokersUsed}")
        console.log("  - Current player canUseJoker(): ${state.currentPlayer.canUseJoker()}")
        console.log("  - Hand size: ${aiHand.size}")
        console.log("  - Hand pins: ${aiHand.map { it.color.name }.joinToString(", ")}")
        
        // Create BoardAnalyzer for AI strategy
        val boardAnalyzer = BoardAnalyzer()
        
        // Generate AI move
        console.log("🔍 Calling ai.generateMove()...")
        val move = aiPlayer?.let { player ->
            console.log("AIGameManager: ${player.name} is thinking (v${ExpertStrategy.VERSION})...")
            player.generateMove(state, aiHand, BoardAnalyzer())
        }
        if (move != null) {
            console.log("AIGameManager: AI placing ${move.pin.color} at ${move.position}")
            try {
                // Calculate score before applying move
                val scoreResult = scoreCalculator.calculateMoveScore(state.board, move)
                updateScoreDisplay(scoreResult.points, ai.id)
                
                // Play placement and score sounds
                SoundManager.playPinPlaced()
                SoundManager.playScoreSound(scoreResult.points)
                if (scoreResult.pinsToReverse.isNotEmpty()) {
                    SoundManager.playPinsReversed()
                }
                
                // Trigger flash
                val flashPositions = mutableListOf(move.position)
                scoreResult.linesFormed.forEach { pattern -> flashPositions.addAll(pattern) }
                flashPositions.addAll(scoreResult.pinsToReverse)
                triggerScoreFlash(flashPositions.distinct())
                
                engine.applyLocalMove(move)
            } catch (e: Exception) {
                console.error("AIGameManager: AI move failed: ${e.message}")
            }
        } else {
            console.error("AIGameManager: AI could not generate a move")
        }
    }
    
    /**
     * Handle player clicking on the board.
     */
    fun handleBoardClick(row: Int, col: Int) {
        // Multi-Path Synchronous Jolt (Phase 5 Nuclear Fix)
        SoundManager.joltAudio()
        
        if (!isPlayerTurn || isGameOver) {
            console.log("AIGameManager: Not player's turn or game over")
            return
        }
        
        val engine = gameEngine ?: return
        val state = engine.getCurrentGameState() ?: return
        val position = Position.fromLegacy(row, col)
        
        if (!state.board.isEmpty(position)) {
            console.log("AIGameManager: Position occupied")
            return
        }
        
        // Save state before move for undo
        lastPlayerMoveState = state
        
        // Check if we have a pending joker to place
        val jokerPin = pendingJokerPin
        if (jokerPin != null) {
            console.log("AIGameManager: Player placing joker ${jokerPin.originalColor} as ${jokerPin.color} at $position")
            try {
                val move = Move(PLAYER_ID, jokerPin, position)
                
                // Calculate score before applying
                val scoreResult = scoreCalculator.calculateMoveScore(state.board, move)
                updateScoreDisplay(scoreResult.points, PLAYER_ID)
                
                // Play placement and score sounds
                SoundManager.playPinPlaced()
                SoundManager.playScoreSound(scoreResult.points)
                if (scoreResult.pinsToReverse.isNotEmpty()) {
                    SoundManager.playPinsReversed()
                }
                
                // Trigger flash
                val flashPositions = mutableListOf(position)
                scoreResult.linesFormed.forEach { pattern -> flashPositions.addAll(pattern) }
                flashPositions.addAll(scoreResult.pinsToReverse)
                triggerScoreFlash(flashPositions.distinct())
                
                engine.applyLocalMove(move)
                pendingJokerPin = null
                selectedPinIndex = 0
            } catch (e: Exception) {
                console.error("AIGameManager: Joker move failed: ${e.message}")
                lastPlayerMoveState = null
            }
            return
        }
        
        // Get selected pin from hand
        val hand = state.currentHand
        if (selectedPinIndex >= hand.size) {
            console.log("AIGameManager: Invalid pin selection")
            lastPlayerMoveState = null
            return
        }
        
        val pin = hand[selectedPinIndex]
        console.log("AIGameManager: Player placing ${pin.color} at $position")
        
        try {
            val move = Move(PLAYER_ID, pin, position)
            
            // Calculate score before applying
            val scoreResult = scoreCalculator.calculateMoveScore(state.board, move)
            updateScoreDisplay(scoreResult.points, PLAYER_ID)
            
            // Play placement and score sounds
            SoundManager.playPinPlaced()
            SoundManager.playScoreSound(scoreResult.points)
            if (scoreResult.pinsToReverse.isNotEmpty()) {
                SoundManager.playPinsReversed()
            }
            
            // Trigger flash
            val flashPositions = mutableListOf(position)
            scoreResult.linesFormed.forEach { pattern -> flashPositions.addAll(pattern) }
            flashPositions.addAll(scoreResult.pinsToReverse)
            triggerScoreFlash(flashPositions.distinct())
            
            engine.applyLocalMove(move)
            selectedPinIndex = 0  // Reset selection after placing
        } catch (e: Exception) {
            console.error("AIGameManager: Player move failed: ${e.message}")
            lastPlayerMoveState = null
        }
    }
    
    /**
     * Select a pin from the player's hand.
     */
    fun selectHandPin(index: Int) {
        // Proactively unlock audio on pin selection (valid user gesture)
        SoundManager.unlockAudio()
        
        val state = gameEngine?.getCurrentGameState() ?: return
        if (index < state.currentHand.size) {
            selectedPinIndex = index
            renderAIHand()
        }
    }
    
    /**
     * Start joker creation by sacrificing a pin.
     */
    fun sacrificePinForJoker(index: Int) {
        // Proactively unlock audio on joker sacrifice (valid user gesture)
        SoundManager.unlockAudio()
        
        if (!isPlayerTurn || isGameOver) return
        val state = gameEngine?.getCurrentGameState() ?: return
        
        // Check if player can still use jokers
        val player = state.players.find { it.id == PLAYER_ID }
        if (player?.canUseJoker() != true) {
            window.alert("You have already used the maximum number of jokers (4) this game!")
            return
        }
        
        if (index >= state.currentHand.size) return
        
        val pin = state.currentHand[index]
        if (pin.isJoker) return  // Can't sacrifice a joker
        
        pendingJokerIndex = index
        renderAIHandWithJokerSelection(index, pin)
    }
    
    /**
     * Select color for the joker.
     */
    fun selectJokerColor(colorName: String) {
        // Proactively unlock audio on joker color selection (valid user gesture)
        SoundManager.unlockAudio()
        
        val index = pendingJokerIndex ?: return
        val state = gameEngine?.getCurrentGameState() ?: return
        if (index >= state.currentHand.size) return
        
        val originalPin = state.currentHand[index]
        val jokerColor = try {
            PinColor.valueOf(colorName.uppercase())
        } catch (e: Exception) {
            console.error("Invalid joker color: $colorName")
            return
        }
        
        // Create joker pin
        pendingJokerPin = Pin.joker(originalColor = originalPin.color, playedAsColor = jokerColor)
        pendingJokerIndex = null
        selectedPinIndex = index  // Select this pin for placement
        
        console.log("AIGameManager: Joker created - ${originalPin.color} playing as $jokerColor")
        renderAIHand()
    }
    
    /**
     * Cancel joker creation.
     */
    fun cancelJoker() {
        pendingJokerIndex = null
        pendingJokerPin = null
        renderAIHand()
    }
    
    /**
     * Render hand with joker color selection UI.
     */
    private fun renderAIHandWithJokerSelection(sacrificeIndex: Int, sacrificedPin: Pin) {
        val container = document.getElementById("ai-hand-pins") ?: return
        
        val html = StringBuilder()
        html.append("<div class=\"joker-selection\">")
        html.append("<div class=\"joker-selection-title\">Playing ${sacrificedPin.color.name} as joker</div>")
        html.append("<div class=\"joker-selection-subtitle\">Choose color:</div>")
        html.append("<div class=\"joker-color-row\">")
        listOf("RED", "GREEN", "PURPLE", "YELLOW").forEach { color ->
            val bgColor = when(color) {
                "RED" -> "#FF6B6B"
                "GREEN" -> "#00FF00"
                "PURPLE" -> "#8B00FF"
                "YELLOW" -> "#FFFF00"
                else -> "#666"
            }
            val textColor = if (color == "GREEN" || color == "YELLOW") "#000" else "#fff"
            html.append("<button class=\"joker-color-option\" style=\"background:$bgColor;color:$textColor\" data-action=\"select-ai-joker-color\" data-color=\"$color\">$color</button>")
        }
        html.append("</div>")
        html.append("<button class=\"joker-cancel-btn\" data-action=\"cancel-ai-joker\">Cancel</button>")
        html.append("</div>")
        
        container.innerHTML = html.toString()
    }
    
    /**
     * Pause the game (for ads).
     */
    private fun pauseGame() {
        console.log("AIGameManager: ⏸️ Pausing game")
        isGamePaused = true
        
        // Cancel any pending AI move
        pendingAITimeoutId?.let {
            window.clearTimeout(it)
            pendingAITimeoutId = null
            console.log("AIGameManager: Cancelled pending AI move")
        }
    }
    
    /**
     * Resume the game (after ad).
     */
    private fun resumeGame() {
        console.log("AIGameManager: ▶️ Resuming game")
        isGamePaused = false
        
        // Check if it's AI's turn and reschedule
        if (!isPlayerTurn && !isGameOver) {
            console.log("AIGameManager: Rescheduling AI move")
            scheduleAIMove()
        }
    }
    
    /**
     * Handle game over.
     */
    private fun handleGameOver(state: GameState) {
        isGameOver = true
        
        // Pause game before showing ad
        pauseGame()
        
        // Show interstitial ad before displaying game over
        co.uk.paxes.pinpoint.web.BannerAdManager.showInterstitialAd {
            console.log("AIGameManager: Interstitial ad closed, showing game over screen")
            displayAIGameOverScreen(state)
        }
    }
    
    /**
     * Display the AI game over screen with final scores.
     */
    private fun displayAIGameOverScreen(state: GameState) {
        val sortedPlayers = state.players.sortedByDescending { it.score }
        val humanScore = state.players.find { it.id == PLAYER_ID }?.score ?: 0
        val aiScore = state.players.find { it.id == aiPlayer?.id }?.score ?: 0
        val winner = sortedPlayers.first()
        val isTie = sortedPlayers.size > 1 && sortedPlayers[0].score == sortedPlayers[1].score

        // Show the Game Over dialog
        val dialog = document.getElementById("game-over-dialog")
        val winnerDisplay = document.getElementById("game-over-winner")
        val scoresList = document.getElementById("game-over-scores")
        
        if (winnerDisplay != null) {
            val winnerHtml = StringBuilder()
            if (!isTie) {
                val isPlayerWinner = winner.id == PLAYER_ID
                winnerHtml.append("<span class=\"winner-label\">Winner</span>")
                winnerHtml.append("<span class=\"winner-name\">${if (isPlayerWinner) "🎉 YOU WIN 🎉" else "${winner.name} Wins!"}</span>")
            } else {
                winnerHtml.append("<span class=\"winner-name\">TIE GAME</span>")
            }
            winnerDisplay.innerHTML = winnerHtml.toString()
        }
        
        if (scoresList != null) {
            val scoresHtml = StringBuilder()
            sortedPlayers.forEach { player ->
                val isMe = player.id == PLAYER_ID
                val displayName = if (player.isAI) "${getDifficultyEmoji(difficulty)} ${player.name}" else player.name
                scoresHtml.append("<div class=\"final-score-row ${if (isMe) "is-me" else ""}\">")
                scoresHtml.append("<span class=\"player-name\">$displayName</span>")
                scoresHtml.append("<span class=\"player-score\">${player.score}</span>")
                scoresHtml.append("</div>")
            }
            scoresList.innerHTML = scoresHtml.toString()
        }
        
        dialog?.asDynamic()?.style?.display = "flex"
        
        val indicator = document.getElementById("ai-turn-indicator")
        indicator?.classList?.remove("thinking", "my-turn", "ai-turn")
        
        when {
            humanScore > aiScore -> {
                indicator?.innerHTML = "🎉 You Win! $humanScore - $aiScore"
                indicator?.classList?.add("my-turn")
            }
            aiScore > humanScore -> {
                indicator?.innerHTML = "😔 AI Wins! $aiScore - $humanScore"
                indicator?.classList?.add("ai-turn")
            }
            else -> {
                indicator?.innerHTML = "🤝 It's a Tie! $humanScore - $aiScore"
            }
        }
    }
    
    /**
     * Quit the current AI game.
     */
    fun quitGame() {
        gameEngine = null
        aiPlayer = null
        isGameOver = true
        NavigationManager.goBack()
    }
    
    /**
     * Set up click handler for AI game board.
     */
    private fun setupBoardClickHandler() {
        val canvas = document.getElementById("ai-game-board") as? HTMLCanvasElement
        canvas?.addEventListener("click", { event ->
            // Proactively unlock audio on board click (valid user gesture)
            SoundManager.unlockAudio()
            
            val mouseEvent = event as MouseEvent
            val rect = canvas.getBoundingClientRect()
            
            // Use the displayed size for calculations (like Main.kt does)
            // This works correctly when CSS scales the canvas
            val displayedWidth = rect.width
            val displayedHeight = rect.height
            val cellSize = displayedWidth / 10.0
            
            val x = mouseEvent.clientX - rect.left
            val y = mouseEvent.clientY - rect.top
            val col = (x / cellSize).toInt().coerceIn(0, 9)
            val row = (y / cellSize).toInt().coerceIn(0, 9)
            
            console.log("AIGameManager: Click at ($x, $y), displayed size: ${displayedWidth}x${displayedHeight}, cell: ($row, $col)")
            
            handleBoardClick(row, col)
        })
    }
    
    /**
     * Render the AI game board.
     */
    private fun renderAIBoard() {
        val canvas = document.getElementById("ai-game-board") as? HTMLCanvasElement ?: return
        val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
        val cellSize = canvas.width / 10.0
        val state = gameEngine?.getCurrentGameState() ?: return
        
        // Dark board background
        ctx.fillStyle = "#2E2E2E"
        ctx.fillRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
        
        // Draw grid lines
        ctx.strokeStyle = "#00FFFF"
        ctx.lineWidth = 3.0
        for (gridLine in listOf(3, 6)) {
            val pos = gridLine * cellSize + cellSize / 2
            ctx.beginPath()
            ctx.moveTo(pos, 0.0)
            ctx.lineTo(pos, canvas.height.toDouble())
            ctx.stroke()
            ctx.beginPath()
            ctx.moveTo(0.0, pos)
            ctx.lineTo(canvas.width.toDouble(), pos)
            ctx.stroke()
        }
        
        // Draw cells
        for (row in 0 until 10) {
            for (col in 0 until 10) {
                val position = Position.fromLegacy(row, col)
                val pin = state.board.getPinAt(position)
                drawAICell(ctx, row, col, pin, cellSize)
            }
        }
        
        // Update cursor based on turn
        canvas.style.cursor = if (isPlayerTurn && !isGameOver) "pointer" else "default"
    }
    
    private fun drawAICell(ctx: CanvasRenderingContext2D, row: Int, col: Int, pin: Pin?, cellSize: Double) {
        val x = col * cellSize + cellSize / 2
        val y = row * cellSize + cellSize / 2
        val holeRadius = cellSize * 0.42
        val pinRadius = cellSize * 0.32
        
        // Draw hole
        ctx.beginPath()
        ctx.arc(x, y, holeRadius, 0.0, 2 * kotlin.math.PI)
        ctx.fillStyle = "#757575"
        ctx.fill()
        ctx.strokeStyle = "#424242"
        ctx.lineWidth = 0.5
        ctx.stroke()
        
        // Draw pin if present
        if (pin != null) {
            val effectiveColor = pin.getEffectiveColor()
            val fillColor = PIN_COLORS[effectiveColor] ?: "#666"
            
            ctx.beginPath()
            ctx.arc(x, y, pinRadius, 0.0, 2 * kotlin.math.PI)
            ctx.fillStyle = fillColor
            ctx.fill()
            ctx.strokeStyle = "rgba(0, 0, 0, 0.3)"
            ctx.lineWidth = 0.5
            ctx.stroke()
            
            if (pin.isJoker) {
                ctx.fillStyle = "#fff"
                ctx.font = "bold ${(cellSize * 0.35).toInt()}px Arial"
                ctx.textAlign = CanvasTextAlign.CENTER
                ctx.textBaseline = CanvasTextBaseline.MIDDLE
                ctx.fillText("J", x, y)
            }
        }
        
        // Draw flash highlight if active for this position
        if (flashingPositions.contains(Position.fromLegacy(row, col)) && isFlashVisible) {
            // Draw flash around the PIN (not the hole) for better visibility
            val flashRadius = if (pin != null) pinRadius * 1.2 else holeRadius * 1.1
            
            ctx.beginPath()
            ctx.arc(x, y, flashRadius, 0.0, 2 * kotlin.math.PI)
            ctx.strokeStyle = "#FFFFFF"
            ctx.lineWidth = 4.0
            ctx.stroke()
            
            // Add a subtle outer glow
            ctx.beginPath()
            ctx.arc(x, y, flashRadius * 1.15, 0.0, 2 * kotlin.math.PI)
            ctx.strokeStyle = "rgba(255, 255, 255, 0.5)"
            ctx.lineWidth = 2.0
            ctx.stroke()
        }
    }
    
    /**
     * Trigger the score flash animation for the given positions.
     * High-contrast white flashing effect matching Android.
     */
    private fun triggerScoreFlash(positions: List<Position>) {
        if (positions.isEmpty()) return
        
        console.log("⚡ [AI Game] Triggering score flash for ${positions.size} pins")
        
        // Cancel any existing flash
        scoreFlashIntervalId?.let { window.clearInterval(it) }
        scoreFlashTimeoutId?.let { window.clearTimeout(it) }
        
        flashingPositions = positions.toSet()
        isFlashVisible = true
        renderAIBoard() // Initial render with flash
        
        // Toggle flash every 400ms for 2 seconds
        scoreFlashIntervalId = window.setInterval({
            isFlashVisible = !isFlashVisible
            renderAIBoard()
        }, 400)
        
        // End flash after 2 seconds
        scoreFlashTimeoutId = window.setTimeout({
            scoreFlashIntervalId?.let { window.clearInterval(it) }
            scoreFlashIntervalId = null
            scoreFlashTimeoutId = null
            flashingPositions = emptySet()
            isFlashVisible = false
            renderAIBoard()
            console.log("⚡ [AI Game] Score flash finished")
        }, 2000)
    }

    /**
     * Update score tracking after a move.
     */
    private fun updateScoreDisplay(points: Int, playerId: String) {
        lastPinScore = points
        if (playerId == PLAYER_ID) {
            handScore += points
        }
    }

    /**
     * Render AI game scores.
     */
    private fun renderAIScores() {
        val panel = document.getElementById("ai-game-scores") ?: return
        val state = gameEngine?.getCurrentGameState() ?: return
        
        val html = StringBuilder()
        for (player in state.players) {
            val isHuman = player.id == PLAYER_ID
            val isCurrentTurn = player.id == state.currentPlayerId
            val itemClass = buildString {
                append("score-item")
                if (isHuman) append(" current-player")
                if (isCurrentTurn) append(" active-turn")
            }
            val displayName = if (player.isAI) "${getDifficultyEmoji(difficulty)} ${player.name}" else player.name
            html.append("<div class=\"$itemClass\">")
            html.append("<div class=\"score-name\">$displayName</div>")
            html.append("<div class=\"score-value\">${player.score}</div>")
            html.append("</div>")
        }
        panel.innerHTML = html.toString()
    }
    
    /**
     * Render player's hand for AI game.
     */
    private fun renderAIHand() {
        val container = document.getElementById("ai-hand-pins") ?: return
        val handContainer = document.getElementById("ai-player-hand") ?: return
        val state = gameEngine?.getCurrentGameState() ?: return
        
        // Show hand when it's player's turn (use isPlayerTurn flag which is updated after moves)
        if (!isPlayerTurn) {
            handContainer.classList.add("not-your-turn")
            container.innerHTML = "<div class=\"loading\">AI is playing...</div>"
            return
        } else {
            handContainer.classList.remove("not-your-turn")
        }

        // Update joker status
        val jokerStatusEl = document.getElementById("ai-joker-status")
        val currentPlayer = state.players.find { it.id == PLAYER_ID }
        if (currentPlayer != null && jokerStatusEl != null) {
            val jokersRemaining = 4 - currentPlayer.jokersUsed
            jokerStatusEl.textContent = "Jokers: $jokersRemaining/4"
        }
        
        // Get the current hand - when it's player's turn, this is the player's hand
        val hand = state.currentHand
        
        if (hand.isEmpty()) {
            container.innerHTML = "<div class=\"loading\">Waiting...</div>"
            return
        }
        
        val html = StringBuilder()
        hand.forEachIndexed { index, pin ->
            val colorClass = pin.color.name.lowercase()
            val selectedClass = if (index == selectedPinIndex) "selected" else ""
            val disabledClass = if (!isPlayerTurn || isGameOver) "disabled" else ""
            
            html.append("<div class=\"hand-pin-container\">")
            html.append("<div class=\"hand-pin $colorClass $selectedClass $disabledClass\" onclick=\"selectAIHandPin($index)\">")
            if (pin.isJoker) {
                html.append("J")
            }
            html.append("</div>")
            // Add JOKER button under each non-joker pin (if player can still use jokers)
            val player = state.players.find { it.id == PLAYER_ID }
            if (!pin.isJoker && isPlayerTurn && !isGameOver && player?.canUseJoker() == true) {
                html.append("<button class=\"joker-btn\" onclick=\"aiSacrificePinForJoker($index)\">JOKER</button>")
            }
            html.append("</div>")
        }
        container.innerHTML = html.toString()
    }
    
    /**
     * Update turn indicator.
     */
    private fun updateAITurnIndicator() {
        val indicator = document.getElementById("ai-turn-indicator") ?: return
        indicator.classList.remove("my-turn", "ai-turn", "thinking")
        
        if (isGameOver) return
        
        // Check if we're in hand completion pause - show countdown
        if (isInHandCompletionPause && handCompletionPauseTimeRemaining > 0) {
            indicator.textContent = "Undo available: ${handCompletionPauseTimeRemaining}s"
            indicator.classList.add("my-turn") // Use my-turn styling for visibility
            return
        }
        
        if (isPlayerTurn) {
            indicator.textContent = "Your turn - tap to place a pin"
            indicator.classList.add("my-turn")
        } else {
            indicator.textContent = "AI's turn"
            indicator.classList.add("ai-turn")
        }
    }
    
    /**
     * Load unlocked difficulties from storage with time-based expiration.
     */
    private fun loadUnlockedDifficulties() {
        try {
            val currentTime = kotlin.js.Date.now().toLong()
            
            // Check Hard difficulty
            val hardUnlockTime = PlayGamaStorageManager.getItem("pinpoint_hard_unlock_time")?.toLongOrNull() ?: 0L  // ✅ Updated: use PlayGamaStorageManager
            if (currentTime < hardUnlockTime) {
                unlockedDifficulties.add(AIDifficulty.HARD)
                console.log("AIGameManager: Hard difficulty loaded (expires at $hardUnlockTime)")
            }
            
            // Check Expert difficulty
            val expertUnlockTime = PlayGamaStorageManager.getItem("pinpoint_expert_unlock_time")?.toLongOrNull() ?: 0L  // ✅ Updated: use PlayGamaStorageManager
            if (currentTime < expertUnlockTime) {
                unlockedDifficulties.add(AIDifficulty.EXPERT)
                console.log("AIGameManager: Expert difficulty loaded (expires at $expertUnlockTime)")
            }
        } catch (e: Exception) {
            console.log("Failed to load unlocked difficulties: ${e.message}")
        }
    }
    
    /**
     * Check if a difficulty is unlocked for the current session only.
     */
    private fun isUnlockedForSession(difficulty: AIDifficulty): Boolean {
        return sessionUnlocks.contains(difficulty)
    }
    
    /**
     * Unlock a difficulty for the current session only (no ads available).
     */
    private fun unlockForSession(difficulty: AIDifficulty) {
        sessionUnlocks.add(difficulty)
        console.log("AIGameManager: $difficulty unlocked for session only")
    }
    
    /**
     * Unlock a difficulty permanently with time-based expiration (ad was watched).
     */
    private fun unlockPermanently(difficulty: AIDifficulty) {
        val currentTime = kotlin.js.Date.now().toLong()
        val unlockTime = when (difficulty) {
            AIDifficulty.HARD -> currentTime + HARD_UNLOCK_DURATION
            AIDifficulty.EXPERT -> currentTime + EXPERT_UNLOCK_DURATION
            else -> return // Easy and Medium don't need unlocking
        }
        
        val storageKey = when (difficulty) {
            AIDifficulty.HARD -> "pinpoint_hard_unlock_time"
            AIDifficulty.EXPERT -> "pinpoint_expert_unlock_time"
            else -> return
        }
        
        try {
            PlayGamaStorageManager.setItem(storageKey, unlockTime.toString())  // ✅ Updated: use PlayGamaStorageManager
            unlockedDifficulties.add(difficulty)
            console.log("AIGameManager: $difficulty unlocked permanently until $unlockTime")
        } catch (e: Exception) {
            console.log("Failed to save unlock time for $difficulty: ${e.message}")
        }
    }

    
    /**
     * Request undo of last player move.
     */
    fun requestUndo() {
        // Proactively unlock audio on undo request (valid user gesture)
        SoundManager.unlockAudio()
        
        if (!canUndo) {
            console.log("AIGameManager: Cannot undo - canUndo=$canUndo")
            return
        }
        
        val savedState = lastPlayerMoveState
        val engine = gameEngine
        if (savedState == null || engine == null) {
            console.log("AIGameManager: No state to restore")
            return
        }
        
        // Check if player has undo credits using UndoCreditManager
        val credits = co.uk.paxes.pinpoint.web.ads.UndoCreditManager.getCredits()
        
        if (credits > 0) {
            console.log("AIGameManager: Player has $credits credits - using one to undo")
            performUndo(savedState, engine)
        } else if (co.uk.paxes.pinpoint.web.ads.UndoCreditManager.isAtMaxCredits()) {
            console.log("AIGameManager: Already at maximum credits")
            window.alert("Maximum credits reached (10/10)")
        } else {
            console.log("AIGameManager: No credits - showing rewarded ad")
            co.uk.paxes.pinpoint.web.ads.FacebookAdService.showRewardedAd(
                placementID = "undo_credit",
                onCompleted = {
                    console.log("AIGameManager: Ad completed! Adding credit and performing undo")
                    co.uk.paxes.pinpoint.web.ads.UndoCreditManager.addCredits(1)
                    updateUndoUI()
                    // Now perform the undo with the newly earned credit
                    if (canUndo && lastPlayerMoveState != null && gameEngine != null) {
                        performUndo(lastPlayerMoveState!!, gameEngine!!)
                    }
                },
                onDismissed = {
                    console.log("AIGameManager: Ad dismissed by user - no credits added")
                    updateUndoUI()
                },
                onFailed = {
                    console.log("AIGameManager: Ad failed")
                    window.alert("Ad unavailable. Try again later.")
                    updateUndoUI()
                }
            )
        }
    }
    
    /**
     * Perform the actual undo operation.
     */
    private fun performUndo(savedState: GameState, engine: GameEngine) {
        val currentCredits = co.uk.paxes.pinpoint.web.ads.UndoCreditManager.getCredits()
        if (currentCredits <= 0) {
            console.log("AIGameManager: No credits available")
            return
        }
        
        console.log("AIGameManager: Undoing last move")
        
        // Cancel countdown if active
        if (turnTransitionTimeoutId != null) {
            console.log("AIGameManager: Cancelling turn transition timeout")
            window.clearTimeout(turnTransitionTimeoutId!!)
            turnTransitionTimeoutId = null
        }
        
        if (countdownIntervalId != null) {
            console.log("AIGameManager: Cancelling countdown interval")
            window.clearInterval(countdownIntervalId!!)
            countdownIntervalId = null
        }
        
        // Reset countdown state
        isInHandCompletionPause = false
        handCompletionPauseTimeRemaining = 0
        
        // Restore the saved state
        engine.restoreState(savedState)
        
        // Use a credit
        co.uk.paxes.pinpoint.web.ads.UndoCreditManager.consumeCredit()
        
        // Clear undo tracking
        canUndo = false
        lastPlayerMove = null
        lastPlayerMoveState = null
        
        // Reset score tracking for this hand (undo the last pin score)
        if (lastPinScore != null) {
            handScore -= lastPinScore!!
            if (handScore < 0) handScore = 0
        }
        lastPinScore = null
        
        // It's still player's turn after undo
        isPlayerTurn = true
        
        // Re-render everything
        renderAIBoard()
        renderAIScores()
        renderAIHand()
        updateAITurnIndicator()
        updateUndoUI()
        renderScoreDisplay()
    }
    
    /**
     * Watch ad to earn undo credit.
     */
    fun watchAdForCredit() {
        // Proactively unlock audio on ad request (valid user gesture)
        SoundManager.unlockAudio()
        
        RewardedAdManager.showRewardedAd(
            onRewardEarned = {
                console.log("AIGameManager: Ad completed, adding undo credit")
                updateUndoUI()
            },
            onAdClosed = {
                console.log("AIGameManager: Ad closed")
            },
            onAdFailed = {
                console.log("AIGameManager: Ad failed")
            },
            adName = "undo_credit"  // CRITICAL: Must pass adName to trigger credit addition
        )
    }
    
    /**
     * Update undo UI elements.
     */
    private fun updateUndoUI() {
        val creditsDisplay = document.getElementById("ai-undo-credits-display")
        val currentCredits = co.uk.paxes.pinpoint.web.ads.UndoCreditManager.getCredits()
        console.log("AIGameManager.updateUndoUI: currentCredits=$currentCredits, will update display with: $currentCredits")
        creditsDisplay?.textContent = currentCredits.toString()
        
        val undoBtn = document.getElementById("ai-btn-undo") as? HTMLButtonElement
        undoBtn?.disabled = !canUndo || currentCredits <= 0
        
        val undoStatus = document.getElementById("ai-undo-status")
        if (isPlayerTurn && !isGameOver) {
            undoStatus?.asDynamic()?.style?.display = "flex"
            if (canUndo && currentCredits > 0) {
                undoBtn?.classList?.add("active")
            } else {
                undoBtn?.classList?.remove("active")
            }
        } else {
            undoStatus?.asDynamic()?.style?.display = "none"
        }
    }
    
    /**
     * Render the score display (pin score / hand score).
     */
    private fun renderScoreDisplay() {
        val container = document.getElementById("ai-score-display") as? HTMLElement ?: return
        val settings = SettingsManager.getSettings()
        
        val showPinScore = settings.showCurrentPinScore
        val showHandScore = settings.showCurrentHandScore
        
        if (!showPinScore && !showHandScore) {
            container.style.display = "none"
            return
        }
        
        container.style.display = "flex"
        
        val html = StringBuilder()
        
        if (showPinScore) {
            val pinScore = lastPinScore ?: 0
            val scoreClass = if (pinScore > 0) "score-positive" else "score-zero"
            html.append("<div class=\"score-chip $scoreClass\">")
            html.append("<span class=\"score-label\">Pin</span>")
            html.append("<span class=\"score-value\">+$pinScore</span>")
            html.append("</div>")
        }
        
        if (showHandScore) {
            val scoreClass = if (handScore > 0) "score-positive" else "score-zero"
            html.append("<div class=\"score-chip $scoreClass\">")
            html.append("<span class=\"score-label\">Hand</span>")
            html.append("<span class=\"score-value\">$handScore</span>")
            html.append("</div>")
        }
        
        container.innerHTML = html.toString()
    }
    
    /**
     * Load undo credits from localStorage.
     */
    private fun loadUndoCredits() {
        // Now handled by RewardedAdManager
    }
    
    /**
     * Save undo credits to localStorage.
     */
    private fun saveUndoCredits() {
        // Now handled by RewardedAdManager
    }
}
